import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface TableQueryState {
  page: number;
  perPage: number;
  sortBy?: string;
  sortDir: 'ASC' | 'DESC';
  filters: Record<string, string>;
  params: Record<string, any>;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  setSort: (key: string) => void;
  setFilter: (key: string, value: string) => void;
  setFilters: (next: Record<string, string>) => void;
  setParam: (key: string, value: string) => void;
  setParams: (next: Record<string, string>) => void;
}

interface TableQueryOptions {
  defaultPerPage?: number;
  // Applied when the matching `filter_<key>` param is absent from the URL, so a
  // page can start with a filter active without writing it to the URL first.
  defaultFilters?: Record<string, string>;
}

// Centralizes page/perPage/sortBy/sortDir/per-column filters in the URL,
// and builds the flat query-param object the backend's parseTableQuery expects.
const useTableQuery = ({ defaultPerPage = 25, defaultFilters }: TableQueryOptions = {}): TableQueryState => {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultFiltersKey = JSON.stringify(defaultFilters || {});

  const page = Number(searchParams.get('page')) || 1;
  const perPage = Number(searchParams.get('perPage')) || defaultPerPage;
  const sortBy = searchParams.get('sortBy') || undefined;
  const sortDir = (searchParams.get('sortDir') === 'ASC' ? 'ASC' : 'DESC') as 'ASC' | 'DESC';

  const filters = useMemo(() => {
    const result: Record<string, string> = { ...JSON.parse(defaultFiltersKey) };
    searchParams.forEach((value, key) => {
      if (key.startsWith('filter_') && value) {
        result[key.replace('filter_', '')] = value;
      }
    });
    return result;
  }, [searchParams, defaultFiltersKey]);

  const update = useCallback((next: Record<string, string | undefined>) => {
    const merged = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      if (value === undefined || value === '') {
        merged.delete(key);
      } else {
        merged.set(key, value);
      }
    });
    setSearchParams(merged);
  }, [searchParams, setSearchParams]);

  const setPage = useCallback((next: number) => {
    update({ page: String(next) });
  }, [update]);

  const setPerPage = useCallback((next: number) => {
    update({ perPage: String(next), page: '1' });
  }, [update]);

  const setSort = useCallback((key: string) => {
    const nextDir = sortBy === key && sortDir === 'ASC' ? 'DESC' : 'ASC';
    update({ sortBy: key, sortDir: nextDir });
  }, [sortBy, sortDir, update]);

  const setFilter = useCallback((key: string, value: string) => {
    update({ [`filter_${key}`]: value, page: '1' });
  }, [update]);

  // Applies several filters in one URL write — separate setFilter calls in the
  // same handler would each build on the same stale searchParams and clobber.
  const setFilters = useCallback((next: Record<string, string>) => {
    const prefixed: Record<string, string> = { page: '1' };
    Object.entries(next).forEach(([key, value]) => {
      prefixed[`filter_${key}`] = value;
    });
    update(prefixed);
  }, [update]);

  // For non-filter params the page owns itself (e.g. a status or tab selector).
  const setParam = useCallback((key: string, value: string) => {
    update({ [key]: value, page: '1' });
  }, [update]);

  // Same, for several raw params at once (filter_ keys may be mixed in).
  const setParams = useCallback((next: Record<string, string>) => {
    update({ ...next, page: '1' });
  }, [update]);

  const params = useMemo(() => {
    const result: Record<string, any> = { page, perPage };
    if (sortBy) {
      result.sortBy = sortBy;
      result.sortDir = sortDir;
    }
    Object.entries(filters).forEach(([key, value]) => {
      result[`filter_${key}`] = value;
    });
    return result;
  }, [page, perPage, sortBy, sortDir, filters]);

  return { page, perPage, sortBy, sortDir, filters, params, setPage, setPerPage, setSort, setFilter, setFilters, setParam, setParams };
};

export default useTableQuery;
